/**
 * 6-Mockup Rotation System - JavaScript Controller
 * SCRIPT 3 Component G.1 & G.2
 * 
 * Features:
 * - Handles thumbnail click events
 * - Manages variant-based mockup switching
 * - Smooth image transitions
 * - Keyboard navigation support
 * - Touch/swipe support for mobile
 */

class MockupRotationController {
  constructor(productId) {
    this.productId = productId;
    this.gallery = document.querySelector(`[data-product-id="${productId}"]`);
    
    if (!this.gallery) {
      console.warn(`Mockup gallery not found for product ${productId}`);
      return;
    }

    this.mainDisplay = this.gallery.querySelector('#mockup-main-display');
    this.thumbnails = this.gallery.querySelectorAll('.mockup-thumbnail:not(.placeholder)');
    this.dataElement = document.getElementById(`mockup-data-${productId}`);
    this.mockupData = this.dataElement ? JSON.parse(this.dataElement.textContent) : null;
    
    this.currentIndex = 0;
    this.currentVariantId = null;
    
    this.init();
  }

  init() {
    this.attachEventListeners();
    this.setupVariantListener();
    this.setupKeyboardNavigation();
    this.setupTouchNavigation();
  }

  attachEventListeners() {
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchMockup(index, thumbnail);
      });
    });
  }

  switchMockup(index, thumbnail) {
    // Remove active class from all thumbnails
    this.thumbnails.forEach(t => t.classList.remove('active'));
    
    // Add active class to clicked thumbnail
    thumbnail.classList.add('active');
    
    // Get image data
    const img = thumbnail.querySelector('img');
    const mainImg = this.mainDisplay.querySelector('.mockup-main-img');
    
    if (img && mainImg) {
      // Fade out effect
      mainImg.style.opacity = '0';
      
      setTimeout(() => {
        // Update image source
        mainImg.src = img.src.replace('width=150', 'width=1200');
        mainImg.alt = img.alt;
        
        // Update srcset for responsive images
        const mediaSrc = img.src.replace('width=150', 'width=1200');
        mainImg.srcset = `
          ${mediaSrc.replace('width=1200', 'width=600')} 600w,
          ${mediaSrc.replace('width=1200', 'width=900')} 900w,
          ${mediaSrc} 1200w
        `;
        
        // Fade in effect
        mainImg.style.opacity = '1';
      }, 300);
    }
    
    this.currentIndex = index;
  }

  setupVariantListener() {
    // Listen for Shopify variant change events
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) {
        this.updateMockupsForVariant(e.detail.variant.id);
      }
    });

    // Fallback: Listen for option selector changes
    const variantSelectors = document.querySelectorAll('[name="id"], .product-form__input');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        const variantId = e.target.value;
        this.updateMockupsForVariant(variantId);
      });
    });
  }

  updateMockupsForVariant(variantId) {
    if (!this.mockupData || !variantId) return;
    
    const variantMockups = this.mockupData.variants[variantId];
    
    if (!variantMockups || variantMockups.mockups.length === 0) {
      console.log('No variant-specific mockups, using defaults');
      return;
    }

    // Update thumbnails with variant-specific mockups
    const mockupsToUse = variantMockups.mockups.length > 0 
      ? variantMockups.mockups 
      : this.mockupData.defaultMockups;

    this.thumbnails.forEach((thumbnail, index) => {
      if (mockupsToUse[index]) {
        const img = thumbnail.querySelector('img');
        if (img) {
          img.src = mockupsToUse[index].thumbnail;
          img.alt = mockupsToUse[index].alt;
        }
        thumbnail.dataset.mediaId = mockupsToUse[index].id;
      }
    });

    // Update main image to first mockup of variant
    if (mockupsToUse[0]) {
      const mainImg = this.mainDisplay.querySelector('.mockup-main-img');
      if (mainImg) {
        mainImg.style.opacity = '0';
        setTimeout(() => {
          mainImg.src = mockupsToUse[0].src;
          mainImg.srcset = `
            ${mockupsToUse[0].srcset['600']} 600w,
            ${mockupsToUse[0].srcset['900']} 900w,
            ${mockupsToUse[0].srcset['1200']} 1200w
          `;
          mainImg.alt = mockupsToUse[0].alt;
          mainImg.style.opacity = '1';
        }, 300);
      }
    }

    this.currentVariantId = variantId;
    this.currentIndex = 0;
    
    // Reset active thumbnail
    this.thumbnails.forEach(t => t.classList.remove('active'));
    if (this.thumbnails[0]) {
      this.thumbnails[0].classList.add('active');
    }
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      // Only handle if gallery is in viewport
      if (!this.isInViewport(this.gallery)) return;

      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.navigatePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.navigateNext();
          break;
      }
    });
  }

  setupTouchNavigation() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.mainDisplay.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.mainDisplay.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left - next image
        this.navigateNext();
      } else {
        // Swipe right - previous image
        this.navigatePrevious();
      }
    }
  }

  navigateNext() {
    const nextIndex = (this.currentIndex + 1) % this.thumbnails.length;
    const nextThumbnail = this.thumbnails[nextIndex];
    if (nextThumbnail && !nextThumbnail.classList.contains('placeholder')) {
      this.switchMockup(nextIndex, nextThumbnail);
    }
  }

  navigatePrevious() {
    const prevIndex = this.currentIndex === 0 
      ? this.thumbnails.length - 1 
      : this.currentIndex - 1;
    const prevThumbnail = this.thumbnails[prevIndex];
    if (prevThumbnail && !prevThumbnail.classList.contains('placeholder')) {
      this.switchMockup(prevIndex, prevThumbnail);
    }
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }
}

// Auto-initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const galleries = document.querySelectorAll('.product-mockup-gallery');
  galleries.forEach(gallery => {
    const productId = gallery.dataset.productId;
    if (productId) {
      new MockupRotationController(productId);
    }
  });
});

// Export for manual initialization if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MockupRotationController;
}
